import type { InfiniteData } from "@tanstack/react-query"
import type { TranslatedContents } from "@/shared/types/type/hospital"
import type { HospitalReviewPage } from "@/entities/hospital/lib/review-prefetch"

export type ReviewTranslationKey = keyof TranslatedContents

/**
 * 리뷰 본문 표시에 쓸 번역 키를 로케일에서 고른다.
 *
 * 화면(Review 몰리큘)과 서버 payload 축소가 반드시 같은 규칙을 봐야 해서 한 곳에 둔다.
 * 둘이 어긋나면 화면이 쓰려는 키를 서버가 지워버려 원문(한국어)으로 떨어진다.
 */
export const resolveReviewTranslationKey = (locale: string): ReviewTranslationKey => {
  if (locale === "ko") return "ko"
  if (locale === "en") return "en"
  if (locale === "ja" || locale === "jp") return "jp"
  if (locale === "zh" || locale === "cn") return "cn"
  return "en"
}

/**
 * 이 로케일 렌더에 실제로 쓰이는 번역 키.
 * = 표시용(요청 로케일) + 원문 토글용 ko. ko 로케일은 둘이 같아 한 개다.
 */
export const requiredReviewTranslationKeys = (locale: string): ReviewTranslationKey[] => {
  const displayKey = resolveReviewTranslationKey(locale)
  return displayKey === "ko" ? ["ko"] : [displayKey, "ko"]
}

/** 남길 키만 고른다. 지울 게 없으면 원본 객체를 그대로 돌려준다(불필요한 복제 방지) */
const pickTranslations = <T extends TranslatedContents | null | undefined>(
  translations: T,
  keys: ReviewTranslationKey[],
): T => {
  // 원본이 없으면 없는 채로 둔다 — 빈 객체를 만들면 원문 토글 조건(Boolean(tr))이 달라진다
  if (translations == null) return translations

  const picked: TranslatedContents = {}
  let dropped = false
  for (const key of Object.keys(translations) as ReviewTranslationKey[]) {
    if (keys.includes(key)) picked[key] = translations[key]
    else dropped = true
  }

  return (dropped ? picked : translations) as T
}

const pruneReview = <T extends { translatedContents?: TranslatedContents } | null | undefined>(
  review: T,
  keys: ReviewTranslationKey[],
): T => {
  if (review == null) return review

  const picked = pickTranslations(review.translatedContents, keys)
  return picked === review.translatedContents ? review : { ...review, translatedContents: picked }
}

/**
 * 초기 HTML(dehydrate)에 실리는 리뷰 payload에서 이 로케일 렌더에 쓰지 않는 번역을 뺀다.
 *
 * 백엔드는 en·jp·cn·ko 4개 번역을 모두 내려주는데, 한 요청이 그리는 화면은
 * 표시용 1개와 원문 토글용 ko만 쓴다. 나머지는 초기 HTML 바이트만 차지한다.
 * 서버 dehydrate 경로 전용이다 — 클라 추가 페이지 응답과 백엔드 요청은 그대로 둔다.
 *
 * 원본 객체는 건드리지 않는다(1페이지 응답은 요청 캐시를 통해 JSON-LD·메타데이터와 공유된다).
 */
export const pruneHospitalReviewTranslations = (
  data: InfiniteData<HospitalReviewPage>,
  locale: string,
): InfiniteData<HospitalReviewPage> => {
  const keys = requiredReviewTranslationKeys(locale)

  return {
    ...data,
    pages: data.pages.map((page) => {
      const result = page?.result
      if (!result) return page

      return {
        ...page,
        result: {
          ...result,
          representativeReview: pruneReview(result.representativeReview, keys),
          ...(result.reviewList
            ? {
              reviewList: {
                ...result.reviewList,
                contents: (result.reviewList.contents ?? []).map((review) =>
                  pruneReview(review, keys),
                ),
              },
            }
            : {}),
        },
      }
    }),
  }
}
